import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import type { OnSubmitCommentReturn } from 'src/components';
import { useCurrentUserId } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import { useCreateCommentReply } from 'src/hooks';

interface UseActivityReplyHandlerArgs {
  commentableId: UUID;
  commentableType: Apollo.CommentableType;
}

/**
 * The "reply to a comment" handler shared by the task and workflow activity surfaces. The
 * `useCreateCommentReply` mutation updates the parent comment's normalized `replies` cache field.
 * The two surfaces differ only in commentableId/commentableType.
 */
export function useActivityReplyHandler({
  commentableId,
  commentableType,
}: UseActivityReplyHandlerArgs): (
  parentCommentId: UUID,
  markdown: string
) => Promise<OnSubmitCommentReturn> {
  const currentUserId = useCurrentUserId();
  const { createCommentReply } = useCreateCommentReply();

  return useCallback(
    async (parentCommentId, markdown) => {
      if (!currentUserId) return null;
      try {
        const { data } = await createCommentReply(parentCommentId, {
          variables: {
            input: {
              commentProps: {
                authorId: currentUserId,
                commentableId,
                commentableType,
                nodeRefId: null,
                parentCommentId,
                text: markdown,
              },
            },
          },
        });
        const comment = data?.createComment.comment ?? null;
        return comment;
      } catch {
        /**
         * A rejected mutate (network error) would otherwise propagate through the reply box's
         * Enter handler unobserved. The global Apollo errorLink surfaces the failure; resolving
         * null keeps the draft so the user can retry.
         */
        return null;
      }
    },
    [commentableId, commentableType, createCommentReply, currentUserId]
  );
}
